/**
 * The agent as DeepAgent draws it: 2px cells, each a slice of the Main Gradient.
 * 3 cells a side for a mark, 9 for the call stage, where its columns follow the voice.
 */

import { LitElement, css, html, type PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";

export type MatrixMode = "idle" | "connecting" | "listening" | "speaking";

interface Cell {
  x: number;
  y: number;
  /** Distance from the centre, 0 at the heart, 1 at the corners. */
  d: number;
  /** Angle around the centre, 0..1. */
  a: number;
}

function buildCells(n: number): Cell[] {
  const center = (n - 1) / 2;
  const max = Math.hypot(center, center) || 1;
  return Array.from({ length: n * n }, (_, i) => {
    const x = i % n;
    const y = Math.floor(i / n);
    return {
      x,
      y,
      d: Math.hypot(x - center, y - center) / max,
      a: (Math.atan2(y - center, x - center) / (2 * Math.PI) + 1) % 1,
    };
  });
}

/** One level per column out of the analyser's bins, loudest in the middle. */
function columnLevels(data: Uint8Array | null | undefined, n: number): number[] {
  const levels = new Array<number>(n).fill(0);
  if (!data || data.length === 0) return levels;
  // Speech energy sits in the lower third of the spectrum.
  const usable = Math.max(n, Math.floor(data.length * 0.35));
  const per = usable / n;
  for (let c = 0; c < n; c++) {
    let sum = 0;
    const from = Math.floor(c * per);
    const to = Math.max(from + 1, Math.floor((c + 1) * per));
    for (let i = from; i < to; i++) sum += data[i] ?? 0;
    levels[c] = sum / (to - from) / 255;
  }
  const sorted = [...levels].sort((p, q) => q - p);
  const center = Math.floor(n / 2);
  const slots = [center];
  for (let k = 1; slots.length < n; k++) {
    if (center - k >= 0) slots.push(center - k);
    if (center + k < n) slots.push(center + k);
  }
  const mirrored = new Array<number>(n).fill(0);
  slots.forEach((slot, rank) => {
    mirrored[slot] = sorted[rank] ?? 0;
  });
  return mirrored;
}

@customElement("deepagent-matrix")
export class DeepAgentMatrix extends LitElement {
  static override styles = css`
    :host {
      --da-matrix-gradient: linear-gradient(
        135deg,
        #fff7bb 0%,
        #ffc66e 22%,
        #ff931e 40%,
        #f15a24 58%,
        #744c9b 78%,
        #456ce8 100%
      );
      display: inline-block;
      width: var(--da-matrix-size, 32px);
      height: var(--da-matrix-size, 32px);
      flex: 0 0 auto;
      position: relative;
      isolation: isolate;
    }

    .grid {
      display: grid;
      grid-template-columns: repeat(var(--n), 1fr);
      gap: var(--da-matrix-gap, 2px);
      width: 100%;
      height: 100%;
    }

    /* The glow the figure casts on the stage: a wide radial, no blur filter. */
    :host([glow]) .grid::before {
      content: "";
      position: absolute;
      inset: -45%;
      z-index: -1;
      pointer-events: none;
      background: radial-gradient(
        closest-side,
        rgba(239, 93, 2, 0.2),
        rgba(239, 93, 2, 0.08) 45%,
        rgba(69, 108, 232, 0.06) 65%,
        transparent
      );
      opacity: 0.7;
      transition: opacity 480ms cubic-bezier(0.16, 1, 0.3, 1);
    }

    :host([glow][data-mode="speaking"]) .grid::before { opacity: 1; }

    .cell {
      border-radius: 2px;
      background: var(--da-matrix-gradient);
      background-size: calc(var(--n) * 100%) calc(var(--n) * 100%);
      background-position:
        calc(var(--x) * 100% / (var(--n) - 1))
        calc(var(--y) * 100% / (var(--n) - 1));
      transition:
        opacity 480ms cubic-bezier(0.16, 1, 0.3, 1),
        transform 480ms cubic-bezier(0.16, 1, 0.3, 1);
    }

    :host([data-mode="idle"]) .cell {
      animation: da-matrix-breathe 3.4s cubic-bezier(0.45, 0, 0.55, 1) infinite alternate;
      animation-delay: calc(var(--d) * -1.7s);
    }

    :host([data-mode="connecting"]) .cell {
      animation: da-matrix-orbit 1.2s linear infinite;
      animation-delay: calc(var(--a) * -1.2s);
    }

    /* The frame loop owns the voice modes and smooths the levels itself. */
    :host([data-mode="listening"]) .cell,
    :host([data-mode="speaking"]) .cell {
      transition: none;
      will-change: opacity, transform;
    }

    @keyframes da-matrix-breathe {
      from { opacity: 0.72; transform: scale(0.9); }
      to   { opacity: 1;    transform: scale(1); }
    }

    @keyframes da-matrix-orbit {
      0%, 100% { opacity: 1;    transform: scale(1); }
      50%      { opacity: 0.18; transform: scale(0.75); }
    }

    @media (prefers-reduced-motion: reduce) {
      :host([data-mode="idle"]) .cell,
      :host([data-mode="connecting"]) .cell {
        animation: none;
      }
    }
  `;

  @property({ type: String, reflect: true, attribute: "data-mode" })
  mode: MatrixMode = "idle";
  /** Cells per side: 3 for a mark, 9 for the call stage. */
  @property({ type: Number }) cells = 3;
  /** The radial glow behind the figure. The stage only. */
  @property({ type: Boolean, reflect: true }) glow = false;
  /** Voice modes only: the analyser to follow. */
  @property({ attribute: false })
  dataSource: (() => Uint8Array | null) | null = null;

  private grid: Cell[] = buildCells(3);
  private frame = 0;

  override willUpdate(changed: PropertyValues<this>): void {
    if (changed.has("cells")) this.grid = buildCells(this.cells);
  }

  override updated(): void {
    const voice = this.mode === "listening" || this.mode === "speaking";
    if (voice && this.dataSource && !this.frame) this.startLoop();
    else if ((!voice || !this.dataSource) && this.frame) this.stopLoop();
  }

  override disconnectedCallback(): void {
    this.stopLoop();
    super.disconnectedCallback();
  }

  /**
   * Attack fast, release slow, so the figure moves with the syllables without
   * flickering. Speaking, the columns follow the agent's voice; listening, one
   * ring grows from the heart with the visitor's.
   */
  private startLoop(): void {
    const n = this.cells;
    const center = (n - 1) / 2;
    const smoothed = new Array<number>(n).fill(0);
    let ring = 0;
    const written = new Array<number>(n * n).fill(-1);
    const follow = (current: number, target: number) =>
      current + (target - current) * (target > current ? 0.55 : 0.12);
    const tick = () => {
      const levels = columnLevels(this.dataSource?.(), n);
      const speaking = this.mode === "speaking";
      if (speaking) {
        // Below saturation, so the loudest column still rises and falls.
        for (let c = 0; c < n; c++) {
          const target = Math.min(1, Math.max(0, (levels[c] ?? 0) - 0.06) * 0.95);
          smoothed[c] = follow(smoothed[c] ?? 0, target);
        }
      } else {
        // A microphone is quieter than the agent's own output.
        const mean = levels.reduce((sum, level) => sum + level, 0) / n;
        ring = follow(ring, Math.min(1, Math.max(0, mean - 0.04) * 2.4));
      }
      const cells = this.renderRoot.querySelectorAll<HTMLElement>(".cell");
      this.grid.forEach((cell, i) => {
        const el = cells[i];
        if (!el) return;
        let level: number;
        if (speaking) {
          const reach = (smoothed[cell.x] ?? 0) * (center + 0.6);
          const offset = Math.abs(cell.y - center);
          const lit = offset <= reach ? 1 : Math.max(0, 1 - (offset - reach));
          level = Math.max(offset < 0.5 ? 0.35 : 0.08, lit);
        } else {
          const reach = 0.18 + ring * 0.95;
          const lit = cell.d <= reach ? 1 : Math.max(0, 1 - (cell.d - reach) * 4);
          level = Math.max(0.12, lit * 0.85);
        }
        level = Math.round(level * 100) / 100;
        if (level === written[i]) return;
        written[i] = level;
        el.style.opacity = String(level);
        el.style.transform = `scale(${0.62 + 0.38 * level})`;
      });
      this.frame = requestAnimationFrame(tick);
    };
    this.frame = requestAnimationFrame(tick);
  }

  private stopLoop(): void {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
    for (const el of this.renderRoot.querySelectorAll<HTMLElement>(".cell")) {
      el.style.opacity = "";
      el.style.transform = "";
    }
  }

  override render() {
    return html`<div class="grid" style=${`--n: ${this.cells}`} aria-hidden="true">
      ${repeat(
        this.grid,
        (cell) => `${cell.x}-${cell.y}`,
        (cell) =>
          html`<span
            class="cell"
            style=${`--x: ${cell.x}; --y: ${cell.y}; --d: ${cell.d.toFixed(3)}; --a: ${cell.a.toFixed(3)}`}
          ></span>`,
      )}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "deepagent-matrix": DeepAgentMatrix;
  }
}
